import { TAction } from '../../actions/types/action';
import {
  TEventHubState,
  THubNewsState,
  TEventHubLatestEventState,
  TEventHubLiveEventState,
  TEventHubEventsHistoryState,
  TEventHubHubCollectionsState,
  TEventHubUpcomingEventsState,
  TEventHubExpansionCollectionsState,
  TGetLiveEventActionParams
} from './types';
import {
  TGetActiveEventsParams,
  TGetEventHistoryParams,
  TGetHubCollectionParams,
  TGetLatestEventsParams,
  TGetUpcomingEventsParams
} from '../../services/commonApiService';

const getInitialState = (): TEventHubState => ({
  expansionCollections: {
    isLoaded: false,
    isLoading: false,
    list: []
  },
  hubCollections: {
    isLoaded: false,
    isLoading: false,
    list: [],
    ui: {}
  },
  liveEvent: {
    isLoaded: false,
    isLoading: false,
    isCoverageLoading: false,
    data: null,
    teamRankings: undefined,
    runByDungeonRankings: [],
    coverageInfo: null,
    isRefreshPaused: false,
    ui: {}
  },
  upcomingEvents: {
    isLoaded: false,
    isLoading: false,
    data: {},
    ui: {}
  },
  activeEvents: {
    isLoaded: false,
    isLoading: false,
    data: {},
    ui: {}
  },
  latestEvent: {
    isLoaded: false,
    isLoading: false,
    data: [],
    ui: {}
  },
  eventsHistory: {
    isLoaded: false,
    isLoading: false,
    data: [],
    ui: {}
  },
  news: {
    isLoaded: false,
    isLoading: false,
    list: [],
    ui: {}
  }
});

// Action Types
const SET_EXPANSION_COLLECTIONS = 'EVENT_HUB/SET_EXPANSION_COLLECTIONS';
const GET_EXPANSION_COLLECTIONS = 'EVENT_HUB/GET_EXPANSION_COLLECTIONS';

const SET_HUB_COLLECTIONS = 'EVENT_HUB/SET_HUB_COLLECTIONS';
const GET_HUB_COLLECTIONS = 'EVENT_HUB/GET_HUB_COLLECTIONS';

const GET_LIVE_EVENT = 'EVENT_HUB/GET_LIVE_EVENT';
const UPDATE_LIVE_EVENT = 'EVENT_HUB/UPDATE_LIVE_EVENT';

const GET_UPCOMING_EVENTS = 'EVENT_HUB/GET_UPCOMING_EVENTS';
const SET_UPCOMING_EVENTS = 'EVENT_HUB/SET_UPCOMING_EVENTS';

const GET_ACTIVE_EVENTS = 'EVENT_HUB/GET_ACTIVE_EVENTS';
const SET_ACTIVE_EVENTS = 'EVENT_HUB/SET_ACTIVE_EVENTS';

const GET_LATEST_EVENT = 'EVENT_HUB/GET_LATEST_EVENT';
const SET_LATEST_EVENT = 'EVENT_HUB/SET_LATEST_EVENT';
const UPDATE_LATEST_EVENT = 'EVENT_HUB/UPDATE_LATEST_EVENT';

const GET_EVENTS_HISTORY = 'EVENT_HUB/GET_EVENTS_HISTORY';
const SET_EVENTS_HISTORY = 'EVENT_HUB/SET_EVENTS_HISTORY';

const GET_NEWS = 'EVENT_HUB/GET_NEWS';
const SET_NEWS = 'EVENT_HUB/SET_NEWS';

// Action Creators
const setExpansionCollections = (payload: TEventHubExpansionCollectionsState) => ({
  type: SET_EXPANSION_COLLECTIONS,
  payload: {
    expansionCollections: payload
  }
});
const getExpansionCollections = () => ({ type: GET_EXPANSION_COLLECTIONS });

const setHubCollections = (payload: TEventHubHubCollectionsState) => ({
  type: SET_HUB_COLLECTIONS,
  payload: { hubCollections: payload }
});
const getHubCollections = (params: TGetHubCollectionParams) => ({ type: GET_HUB_COLLECTIONS, payload: params });

const getLiveEvent = (params: TGetLiveEventActionParams, keepPreviousData = false) => ({
  type: GET_LIVE_EVENT,
  payload: { params, keepPreviousData }
});
const updateLiveEventState = (payload: Partial<TEventHubLiveEventState>) => ({
  type: UPDATE_LIVE_EVENT,
  payload: {
    liveEvent: payload
  }
});

const getUpcomingEvents = (params: TGetUpcomingEventsParams) => ({ type: GET_UPCOMING_EVENTS, payload: params });
const setUpcomingEvents = (payload: TEventHubUpcomingEventsState) => ({
  type: SET_UPCOMING_EVENTS,
  payload: {
    upcomingEvents: payload
  }
});

const getActiveEvents = (params: TGetActiveEventsParams) => ({ type: GET_ACTIVE_EVENTS, payload: params });
const setActiveEvents = (payload: TEventHubUpcomingEventsState) => ({
  type: SET_ACTIVE_EVENTS,
  payload: {
    activeEvents: payload
  }
});

const getLatestEvent = (params: TGetLatestEventsParams) => ({ type: GET_LATEST_EVENT, payload: params });
const setLatestEvent = (payload: TEventHubLatestEventState) => ({
  type: SET_LATEST_EVENT,
  payload: {
    latestEvent: payload
  }
});
const updateLatestEventState = (payload: Partial<TEventHubLatestEventState>) => ({
  type: UPDATE_LATEST_EVENT,
  payload: {
    latestEvent: payload
  }
});

const getEventsHistory = (params: TGetEventHistoryParams) => ({
  type: GET_EVENTS_HISTORY,
  payload: params
});
const setEventsHistoryByExpansion = (payload: TEventHubEventsHistoryState) => ({
  type: SET_EVENTS_HISTORY,
  payload: {
    eventsHistory: payload
  }
});

const getNews = (params: any) => ({ type: GET_NEWS, payload: params });

const setNews = (payload: THubNewsState) => ({
  type: SET_NEWS,
  payload: {
    news: payload
  }
});

// Reducer
export function eventHubReducer(state = getInitialState(), action: TAction<any>) {
  switch (action.type) {
    case SET_HUB_COLLECTIONS:
    case SET_UPCOMING_EVENTS:
    case SET_LATEST_EVENT:
    case SET_EVENTS_HISTORY:
    case SET_NEWS:
    case SET_ACTIVE_EVENTS:
    case SET_EXPANSION_COLLECTIONS: {
      return {
        ...state,
        ...action.payload
      };
    }
    case UPDATE_LIVE_EVENT: {
      return {
        ...state,
        liveEvent: {
          ...state.liveEvent,
          ...action.payload.liveEvent
        }
      };
    }
    case UPDATE_LATEST_EVENT: {
      return {
        ...state,
        latestEvent: {
          ...state.latestEvent,
          ...action.payload.latestEvent
        }
      };
    }
    default:
      return state;
  }
}

export const eventHubActions = {
  setExpansionCollections,
  getExpansionCollections,
  setHubCollections,
  getHubCollections,
  getLiveEvent,
  getActiveEvents,
  setActiveEvents,
  updateLiveEventState,
  getUpcomingEvents,
  setUpcomingEvents,
  getLatestEvent,
  setLatestEvent,
  updateLatestEventState,
  getEventsHistory,
  setEventsHistoryByExpansion,
  getNews,
  setNews
};

export const eventHubActionTypes = {
  SET_EXPANSION_COLLECTIONS,
  GET_EXPANSION_COLLECTIONS,

  SET_HUB_COLLECTIONS,
  GET_HUB_COLLECTIONS,

  GET_LIVE_EVENT,
  UPDATE_LIVE_EVENT,

  GET_UPCOMING_EVENTS,
  SET_UPCOMING_EVENTS,

  GET_ACTIVE_EVENTS,
  SET_ACTIVE_EVENTS,

  GET_LATEST_EVENT,
  SET_LATEST_EVENT,

  GET_EVENTS_HISTORY,
  SET_EVENTS_HISTORY,

  GET_NEWS,
  SET_NEWS
};
